import { getLog } from '@aurora/shared-utils/log';
import React, { useRef } from 'react';
import { Form, useClassNameMapper } from 'react-bootstrap';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import type { FieldPath } from 'react-hook-form/dist/types/path';
import { useUIDSeed } from 'react-uid';
import {
  isCategorizedPossibleValuesField,
  isFieldVisible
} from '../../../helpers/form/FormHelper/FormHelper';
import ComponentHelper from '../../../helpers/ui/ComponentHelper/ComponentHelper';
import type IconTypes from '../../../public/static/graphics/processed/enums';
import { IconColor, IconSize } from '../../common/Icon/enums';
import Icon from '../../common/Icon/Icon';
import InfoPopover from '../../common/InfoPopover/InfoPopover';
import { FormGroupFieldType, type FormFieldVariant } from '../enums';
import FormField from '../FormField/FormField';
import type { FormFieldProps, FormFieldSpecDefinition, PossibleValuesField } from '../types';
import { FieldType } from '../types';
import type { FieldSetType } from '../FormFieldSet/FormFieldset';
import FormFieldset from '../FormFieldSet/FormFieldset';
import { useFormWatch } from '../useFormWatch';

const log = getLog(module);

/**
 * Represents a variation of `FieldSetType` where the `type` and `id` properties are optional.
 *
 * @template DataT - The type of data associated with the field set.
 * @remarks
 * This type omits the `type` and `id` properties from `FieldSetType<DataT>`, and then
 * reintroduces them as optional properties. Useful for scenarios where these fields
 * may not be required during initialization or partial updates.
 */
export type OptionalTypeFieldSet<DataT> = Omit<FieldSetType<DataT>, 'type' | 'id'> & {
  type?: FieldSetType<DataT>['type'];
  id?: FieldSetType<DataT>['id'];
};

/**
 * Radio field specification. NameT is the name of the field and DataT is the definition of bean
 * that holds all the fields for the form.
 */
export interface RadioFieldSpec<NameT extends FieldPath<DataT>, DataT extends FieldValues>
  extends PossibleValuesField<NameT, DataT, FormFieldVariant.RADIO> {
  /**
   * Whether or not the radios are inline
   */
  isInline?: boolean;
  /**
   * Callback to run on Radio field input change.
   */
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement>,
    formMethods: UseFormReturn<DataT>
  ) => void;

  /**
   * Class name for the input element
   */
  inputClassName?: string;
  /**
   * Fieldset specification for the radio field.
   * This is used to render the fieldset around the radio buttons.
   */
  fieldsetSpec?: OptionalTypeFieldSet<DataT>;
}
/**
 * Renders a radio field wrapped inside a fieldset in order to group the radio buttons.
 * The fieldset is rendered with a legend that is the label of the field.
 *
 * It is wrapped inside a fieldset to ensure the component's accessibility is correct.
 *
 * @author Manish Shrestha, Willi Hyde
 */
const RadioField = <NameT extends FieldPath<DataT>, DataT extends FieldValues>({
  fieldSpec,
  formId,
  i18n,
  className,
  formMethods,
  onFormSubmit,
  submitOnChange,
  allFields
}: FormFieldProps<NameT, DataT, RadioFieldSpec<NameT, DataT>>): React.ReactElement => {
  const uidSeed = useUIDSeed();
  const cx = useClassNameMapper();
  const {
    formState: { errors },
    control
  } = formMethods;
  const { hasMessage, formatMessage, loading: textLoading } = i18n;
  const {
    name,
    values,
    attributes,
    focus,
    focusHandler,
    disabled,
    defaultValue,
    isInline = false,
    formGroupSpec,
    onChange: onChangeCallback,
    inputClassName,
    fieldsetSpec,
    isVisible
  } = fieldSpec;
  const hasBeenFocused = useRef(false);

  const { watchFields } = isVisible || {};
  const watchedValuesObject: Partial<DataT> = useFormWatch(watchFields, control);

  const modifiedFieldSpec: RadioFieldSpec<NameT, DataT> = {
    ...fieldSpec,
    formGroupSpec: {
      ...formGroupSpec,
      label: false
    }
  };

  if (
    !isFieldVisible<DataT>(
      fieldSpec as unknown as FormFieldSpecDefinition<DataT>,
      watchedValuesObject,
      allFields
    )
  ) {
    return null;
  }

  if (isCategorizedPossibleValuesField(values)) {
    log.error('Categorized possible value field is not yet supported');
    return null;
  }

  /**
   * Function to render icon beside label
   */
  function renderIcon(icon: IconTypes): React.ReactNode {
    return <Icon icon={icon} size={IconSize.PX_14} color={IconColor.GRAY_900} />;
  }

  if (textLoading) {
    return null;
  }

  return (
    <FormFieldset
      fieldSetSpec={{
        id: fieldsetSpec?.id || `${name}Group`,
        ...fieldsetSpec,
        type: FormGroupFieldType.FIELDSET,
        legend: {
          ...fieldsetSpec?.legend,
          label: fieldsetSpec?.legend?.label,
          className: cx('form-label', fieldsetSpec?.legend?.className)
        }
      }}
      formId={formId}
      i18n={i18n}
    >
      <FormField
        fieldSpec={modifiedFieldSpec}
        fieldType={FieldType.UNCONTROLLED}
        formId={formId}
        i18n={i18n}
        formMethods={formMethods}
        onFormSubmit={onFormSubmit}
        submitOnChange={submitOnChange}
        allFormFields={allFields}
        className={cx(className)}
      >
        {({
          render: {
            register: { ref, onBlur },
            onChange
          }
        }) => {
          const hasFieldTitle = hasMessage(`${formId}.${name}.fieldTitle`);
          return (
            <>
              {hasFieldTitle && (
                <div className={cx('lia-g-mb-15')}>
                  <h4>{formatMessage(`${formId}.${name}.fieldTitle`)}</h4>
                </div>
              )}
              {values?.map((option, index) => {
                const { label, description, key, icon, value, className: optionClassName } = option;
                const itemKey = `${formId}.${name}.${key}`;
                const optionId = uidSeed(itemKey);
                const helpTextValue =
                  description &&
                  (description === true ? formatMessage(`${itemKey}.description`) : description);
                const infoTextKey = `${itemKey}.info`;
                const showInfo = hasMessage(infoTextKey);

                return (
                  <Form.Check key={itemKey} inline={isInline} className={optionClassName}>
                    <Form.Check.Input
                      name={name}
                      type="radio"
                      id={optionId}
                      ref={(element): void => {
                        ref(element);
                        if (
                          focus &&
                          (value === defaultValue || (defaultValue === '' && index === 0))
                        ) {
                          ComponentHelper.focusHandler(hasBeenFocused, element, focusHandler);
                        }
                      }}
                      value={key}
                      isInvalid={errors[name] !== undefined}
                      disabled={disabled}
                      onChange={event => {
                        if (onChangeCallback) {
                          onChangeCallback(event, formMethods);
                        }
                        onChange(event);
                      }}
                      onBlur={onBlur}
                      className={cx(inputClassName)}
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...attributes}
                      data-testid={`RadioField.${itemKey}`}
                    />
                    <div className={cx('d-inline-flex align-items-center lia-g-column-gap-5')}>
                      <Form.Check.Label htmlFor={optionId}>
                        {label || formatMessage(`${itemKey}.label`)}
                      </Form.Check.Label>
                      {showInfo && (
                        <InfoPopover
                          infoText={formatMessage(infoTextKey)}
                          fieldName={label || formatMessage(`${itemKey}.label`)}
                        />
                      )}
                      {icon && renderIcon(icon)}
                    </div>
                    {helpTextValue && <Form.Text>{helpTextValue}</Form.Text>}
                  </Form.Check>
                );
              })}
            </>
          );
        }}
      </FormField>
    </FormFieldset>
  );
};

export default RadioField;
